import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MusicalNoteIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import showService from '../../services/ShowService';
import ShowCard from '../widgets/ShowCard';
import FilterBar from '../widgets/FilterBar';
import NotificationBell from '../widgets/NotificationBell';
import HamburgerMenu from '../widgets/HamburgerMenu';
import AuthButtons from '../widgets/AuthButtons';
import Logo from '../widgets/Logo';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: ${props => props.theme.zIndex.sticky};
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.md};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoContainer = styled.div`
  cursor: pointer;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const HeroSection = styled.section`
  position: relative;
  height: 300px;
  background: linear-gradient(
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.3)
  ), url(${props => props.$bgImage || '/assets/hero.jpg'});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${props => props.theme.colors.primary}40,
      ${props => props.theme.colors.accentSecondary}40
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
  padding: ${props => props.theme.spacing.xl};
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h2`
  font-size: ${props => props.theme.typography.h1.fontSize};
  font-weight: ${props => props.theme.typography.h1.fontWeight};
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.body.fontSize};
  opacity: 0.9;
  line-height: 1.5;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const ControlsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ShowsGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.surfaceVariant};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 32px;
    height: 32px;
    color: ${props => props.theme.colors.text.muted};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg};
`;

const PageButton = styled.button`
  background: ${props => props.active ? props.theme.colors.accent : props.theme.colors.surface};
  color: ${props => props.active ? 'white' : props.theme.colors.text.primary};
  border: 1px solid ${props => props.active ? props.theme.colors.accent : props.theme.colors.border};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? props.theme.colors.accent : props.theme.colors.surfaceVariant};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.small.fontSize};
  margin: 0 ${props => props.theme.spacing.md};
`;

const ShowsCount = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.small.fontSize};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.md};
`;

// Random image selection for hero section (Home page specific)
const homeImages = [
  '/assets/cory.jpeg',
  '/assets/cory2.jpeg',
  '/assets/hero-img.jpg',
  '/assets/hero.jpg'
];

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState({ city: 'Gainesville', state: 'FL' });
  const [currentRadius, setCurrentRadius] = useState(25);
  const [bgImage, setBgImage] = useState('/assets/hero.jpg');
  const [currentPage, setCurrentPage] = useState(1);
  const [showsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    city: 'Gainesville',
    state: 'FL',
    genre: '',
    radius: 25
  });

  React.useEffect(() => {
    const randomImage = homeImages[Math.floor(Math.random() * homeImages.length)];
    setBgImage(`${randomImage}?t=${new Date().getTime()}`);
  }, []);

  // Load user's location from profile when component mounts
  useEffect(() => {
    const loadUserLocation = async () => {
      if (isAuthenticated && user) {
        try {
          const userProfile = await apiService.getUserProfile(user.id);
          if (userProfile && userProfile.city && userProfile.state) {
            const userLocation = { 
              city: userProfile.city, 
              state: userProfile.state 
            };
            setCurrentLocation(userLocation);
            setFilters(prev => ({
              ...prev,
              city: userProfile.city,
              state: userProfile.state
            }));
            // Update ShowService location
            await showService.changeLocation(userProfile.city, userProfile.state);
          }
        } catch (error) {
          console.error('Error loading user location:', error);
          // Keep default location if there's an error
        }
      }
    };

    loadUserLocation();
  }, [isAuthenticated, user]);

  // Database hook: Listen to ShowService changes
  useEffect(() => {
    const unsubscribe = showService.addListener((shows, loading, location) => {
      setShows(shows || []);
      setLoading(loading);
      if (location) {
        setCurrentLocation(location);
        setFilters(prev => ({
          ...prev,
          city: location.city,
          state: location.state
        }));
      }
    });

    // Initial load
    setShows(showService.shows);
    setLoading(showService.isLoading);
    setCurrentLocation(showService.currentLocation);
    setCurrentRadius(showService.currentRadius);

    return () => {
      showService.removeListener(unsubscribe);
    };
  }, []); // Only run once on mount

  // Database hook: Handle location changes
  const handleLocationChange = useCallback(async (city, state) => {
    console.log('📍 Location changed to:', city, state);
    setCurrentLocation({ city, state });
    setCurrentPage(1);
  }, []);

  // Database hook: Handle radius changes
  const handleRadiusChange = useCallback(async (newRadius) => {
    console.log('🌍 Radius changed to:', newRadius, 'miles');
    setCurrentRadius(newRadius);
    setCurrentPage(1);
  }, []);

  // Database hook: Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));

    // Handle radius changes specifically
    if (newFilters.radius && newFilters.radius !== currentRadius) {
      handleRadiusChange(newFilters.radius);
    }

    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [currentRadius, handleRadiusChange]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.genre, filters.dateFrom, filters.dateTo, filters.posterType]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    if (showService.paginationInfo?.hasNextPage) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (showService.paginationInfo?.hasPreviousPage) {
      goToPage(currentPage - 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const totalPages = showService.paginationInfo?.totalPages || 0;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        // Near start: show 1, 2, 3, 4, 5, ..., last
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        if (totalPages > 5) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        // Near end: show 1, ..., last-4, last-3, last-2, last-1, last
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle: show 1, ..., current-1, current, current+1, ..., last
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  useEffect(() => {
    if (currentLocation && !loading) {
      console.log('🏠 Home: Loading shows with pagination');
      showService.loadShowsPaginated(currentLocation, currentRadius, currentPage, showsPerPage, filters);
    }
  }, [currentLocation, currentRadius, currentPage, showsPerPage, filters]);

  return (
    <Container>
      <Header>
        <HeaderContent>
          <LogoContainer onClick={() => navigate('/')}>
            <Logo />
          </LogoContainer>
          
          <HeaderActions>
            {!isAuthenticated && <AuthButtons />}
            <NotificationBell />
            <HamburgerMenu />
          </HeaderActions>
        </HeaderContent>
      </Header>

      <HeroSection $bgImage={bgImage}>
        <HeroContent>
          <HeroTitle>Discover Live Music</HeroTitle>
          <HeroSubtitle>
            Find the best shows in your area and never miss a concert again
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <ControlsSection>
          <FilterBar 
            filters={filters} 
            onFilterChange={handleFilterChange}
            currentLocation={currentLocation}
            onLocationChange={handleLocationChange}
          />
        </ControlsSection>

        {loading ? (
          <LoadingState>
            <div>Loading shows...</div>
          </LoadingState>
        ) : shows.length === 0 && !loading ? (
          <EmptyState>
            <EmptyIcon>
              <MusicalNoteIcon />
            </EmptyIcon>
            <h3>No shows found</h3>
            <p>Try adjusting your filters or check back later for new shows.</p>
          </EmptyState>
        ) : (
          <>
            <ShowsCount>
              Showing {shows.length > 0 ? (currentPage - 1) * showsPerPage + 1 : 0}-{(currentPage - 1) * showsPerPage + shows.length} of {showService.paginationInfo?.totalShows || 0} shows
            </ShowsCount>
            <ShowsGrid>
              {shows.map(show => (
                <ShowCard
                  key={show.id}
                  show={show}
                />
              ))}
            </ShowsGrid>

            {showService.paginationInfo?.totalPages > 1 && (
              <PaginationContainer>
                <PageButton onClick={goToPreviousPage} disabled={!showService.paginationInfo?.hasPreviousPage}>
                  Previous
                </PageButton>
                
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <PageInfo key={`ellipsis-${index}`}>...</PageInfo>
                  ) : (
                    <PageButton
                      key={page}
                      active={page === currentPage}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </PageButton>
                  )
                ))}
                
                <PageButton onClick={goToNextPage} disabled={!showService.paginationInfo?.hasNextPage}>
                  Next
                </PageButton>
              </PaginationContainer>
            )}
          </>
        )}
      </MainContent>
    </Container>
  );
};

export default Home; 